import {
  type AggEnum,
  dashboardReportDetailCreate,
  dashboardReportFieldsRetrieve,
  dashboardReportFieldValuesCreate,
  dashboardReportRunsCreate,
  dashboardReportRunsDownloadCsvRetrieve,
  dashboardReportRunsList,
  dashboardReportRunsRetrieve,
  dashboardSavedReportsCreate,
  dashboardSavedReportsDestroy,
  dashboardSavedReportsList,
  dashboardSavedReportsPartialUpdate,
  dashboardSavedReportsSetDefaultCreate,
  dashboardSavedReportsUnsetDefaultCreate,
  type PatchedSavedReportRequest,
  dashboardReportSummaryCreate,
  type GrainEnum,
  type ReportColumn,
  type ReportDetailRequestRequest,
  type ReportDetailResponse,
  type ReportDetailResponseRowsItem,
  type ReportFieldsResponse,
  type ReportFieldValuesRequestRequest,
  type ReportFieldValuesResponse,
  type ReportRun,
  type ReportRunPage,
  type ReportRunRequestRequest,
  type SavedReport,
  type SavedReportRequest,
  type ReportSummaryRequestRequest,
  type ReportSummaryResponse,
} from "@sidegigllc/api/client";
import { keepPreviousData, mutationOptions, queryOptions, skipToken } from "@tanstack/react-query";

export type DetailRunResult = { columns: Array<ReportColumn>; rows: Array<ReportDetailResponseRowsItem> };

const isDetailResult = (result: unknown): result is DetailRunResult =>
  typeof result === "object" &&
  result !== null &&
  "columns" in result &&
  Array.isArray(result.columns) &&
  "rows" in result &&
  Array.isArray(result.rows);

export const detailResultOf = (run: ReportRun): DetailRunResult | undefined =>
  run.mode === "detail" && isDetailResult(run.result) ? run.result : undefined;

export type SummaryDimensionMeta = { field: string; label: string; type: string; grain: GrainEnum | null };

export type SummaryMeasureMeta = { key: string; field: string; agg: AggEnum; label: string; type: string };

export type SummaryValues = Record<string, unknown>;

export type SummaryRow = {
  kind: "grand_total" | "subtotal" | "group";
  level: number;
  keys: Record<string, unknown>;
  values: SummaryValues | null;
  cells?: Array<SummaryValues | null>;
};

export type SummaryResult = {
  row_fields: Array<SummaryDimensionMeta>;
  col_fields: Array<SummaryDimensionMeta>;
  values: Array<SummaryMeasureMeta>;
  col_keys: Array<Record<string, unknown>>;
  rows: Array<SummaryRow>;
};

export const isSummaryResult = (result: unknown): result is SummaryResult =>
  typeof result === "object" &&
  result !== null &&
  "row_fields" in result &&
  Array.isArray(result.row_fields) &&
  "col_fields" in result &&
  Array.isArray(result.col_fields) &&
  "values" in result &&
  Array.isArray(result.values) &&
  "col_keys" in result &&
  Array.isArray(result.col_keys) &&
  "rows" in result &&
  Array.isArray(result.rows);

export const summaryResultOf = (run: ReportRun): SummaryResult | undefined =>
  run.mode === "summary" && isSummaryResult(run.result) ? run.result : undefined;

// The generated keys omit the location (it travels in a header), so a location switch would serve the
// previous location's cache.
export const reportFieldsQuery = ({ locationId }: { locationId: number }) =>
  queryOptions<ReportFieldsResponse>({
    queryKey: ["dashboard-report-fields", locationId],
    queryFn: ({ signal }) => dashboardReportFieldsRetrieve({ signal }),
  });

export const reportDetailQuery = ({
  locationId,
  request,
}: {
  locationId: number;
  request: ReportDetailRequestRequest;
}) =>
  queryOptions<ReportDetailResponse>({
    queryKey: ["dashboard-report-detail", locationId, request],
    queryFn: ({ signal }) => dashboardReportDetailCreate(request, { signal }),
    placeholderData: keepPreviousData,
  });

export const reportSummaryQuery = ({
  locationId,
  request,
}: {
  locationId: number;
  request: ReportSummaryRequestRequest;
}) =>
  queryOptions<ReportSummaryResponse>({
    queryKey: ["dashboard-report-summary", locationId, request],
    queryFn: ({ signal }) => dashboardReportSummaryCreate(request, { signal }),
    placeholderData: keepPreviousData,
  });

export const reportFieldValuesQuery = ({
  locationId,
  request,
}: {
  locationId: number;
  request: ReportFieldValuesRequestRequest | undefined;
}) =>
  queryOptions<ReportFieldValuesResponse>({
    queryKey: ["dashboard-report-field-values", locationId, request],
    queryFn: request === undefined ? skipToken : ({ signal }) => dashboardReportFieldValuesCreate(request, { signal }),
    enabled: request !== undefined,
  });

export const reportRunsQuery = ({ locationId }: { locationId: number }) =>
  queryOptions<ReportRunPage>({
    queryKey: ["dashboard-report-runs", locationId],
    queryFn: ({ signal }) => dashboardReportRunsList({ signal }),
  });

export const reportRunQuery = ({ locationId, runId }: { locationId: number; runId: string | undefined }) =>
  queryOptions<ReportRun>({
    queryKey: ["dashboard-report-run", locationId, runId],
    queryFn: runId === undefined ? skipToken : ({ signal }) => dashboardReportRunsRetrieve(runId, { signal }),
    enabled: runId !== undefined,
  });

export const generateReportRunMutation = ({ locationId }: { locationId: number }) =>
  mutationOptions({
    mutationKey: ["dashboard-report-run-generate", locationId],
    mutationFn: (request: ReportRunRequestRequest) => dashboardReportRunsCreate(request),
  });

export const downloadReportRunCsv = (runId: string) => dashboardReportRunsDownloadCsvRetrieve(runId, { format: "csv" });

export const savedReportsQuery = ({ locationId }: { locationId: number }) =>
  queryOptions<Array<SavedReport>>({
    queryKey: ["dashboard-saved-reports", locationId],
    queryFn: ({ signal }) => dashboardSavedReportsList({ signal }),
  });

export const createSavedReportMutation = ({ locationId }: { locationId: number }) =>
  mutationOptions({
    mutationKey: ["dashboard-saved-report-create", locationId],
    mutationFn: (request: SavedReportRequest) => dashboardSavedReportsCreate(request),
  });

export const updateSavedReportMutation = ({ locationId }: { locationId: number }) =>
  mutationOptions({
    mutationKey: ["dashboard-saved-report-update", locationId],
    mutationFn: ({ id, ...request }: PatchedSavedReportRequest & { id: number }) =>
      dashboardSavedReportsPartialUpdate(id, request),
  });

export const deleteSavedReportMutation = ({ locationId }: { locationId: number }) =>
  mutationOptions({
    mutationKey: ["dashboard-saved-report-delete", locationId],
    mutationFn: (id: number) => dashboardSavedReportsDestroy(id),
  });

export const setDefaultSavedReportMutation = ({ locationId }: { locationId: number }) =>
  mutationOptions({
    mutationKey: ["dashboard-saved-report-set-default", locationId],
    mutationFn: (id: number) => dashboardSavedReportsSetDefaultCreate(id),
  });

export const unsetDefaultSavedReportMutation = ({ locationId }: { locationId: number }) =>
  mutationOptions({
    mutationKey: ["dashboard-saved-report-unset-default", locationId],
    mutationFn: (id: number) => dashboardSavedReportsUnsetDefaultCreate(id),
  });
